import { useSearchParams, Link } from 'react-router-dom';
import { Mail } from 'lucide-react';

import {
  useOrderDeskList,
  HOLD_REASON_LABELS,
  STATUS_LABELS,
  INTENT_LABELS,
  type OrderDeskRow,
} from '@/hooks/admin/useOrderDesk';
import { cn } from '@/lib/utils';

/**
 * /admin/order-desk — the Order Desk queue.
 *
 * Staff forward a customer's email to the order-desk address instead of
 * hand-typing the order; each one lands here with what we read out of it. The
 * proposal email's Accept button and this page's Submit both go through the
 * same path, so a row can be finished from an inbox or from here.
 *
 * Decisions live at /admin/order-desk/:intakeId.
 */

const FILTERS: Array<{ key: string; label: string }> = [
  { key: 'needs_review', label: 'Needs a decision' },
  { key: 'submitted', label: 'Sent' },
  { key: 'service_filed', label: 'Service calls' },
  { key: 'discarded', label: 'Not orders' },
  { key: 'ignored', label: 'Ignored' },
  { key: 'all', label: 'All' },
];

const BADGE = 'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold';

function statusBadge(row: OrderDeskRow) {
  switch (row.status) {
    case 'submitted':
      return <span className={cn(BADGE, 'bg-emerald-100 text-emerald-700')}>Sent</span>;
    case 'service_filed':
      return <span className={cn(BADGE, 'bg-emerald-100 text-emerald-700')}>Service filed</span>;
    case 'needs_review':
      return (
        <span
          className={cn(
            BADGE,
            row.hold_reason === 'awaiting_accept'
              ? 'bg-sky-100 text-sky-700'
              : 'bg-amber-100 text-amber-700',
          )}
        >
          {HOLD_REASON_LABELS[row.hold_reason ?? ''] ?? 'Needs a decision'}
        </span>
      );
    case 'discarded':
      return <span className={cn(BADGE, 'bg-slate-200 text-slate-600')}>Not an order</span>;
    case 'ignored':
      return <span className={cn(BADGE, 'bg-slate-200 text-slate-600')}>Ignored</span>;
    case 'failed':
      return <span className={cn(BADGE, 'bg-red-100 text-red-700')}>Failed</span>;
    default:
      return <span className={cn(BADGE, 'bg-slate-100 text-slate-600')}>{STATUS_LABELS[row.status] ?? row.status}</span>;
  }
}

function confidenceChip(row: OrderDeskRow) {
  if (!row.customer_name) {
    return <span className={cn(BADGE, 'bg-amber-100 text-amber-700')}>No account</span>;
  }
  if (row.identity_confidence === 'high') return null;
  return <span className={cn(BADGE, 'bg-amber-50 text-amber-700')}>Confirm account</span>;
}

function fmtWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function AdminOrderDesk() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? 'needs_review';

  const { data, isLoading } = useOrderDeskList({ status });
  const rows = data?.rows ?? [];

  return (
    <div className="px-6 py-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-semibold text-brix-ink flex items-center gap-2">
            <Mail size={16} className="text-brix-primary" />
            Order desk — forwarded emails
          </h2>
          <p className="text-sm text-brix-muted mt-0.5 max-w-2xl">
            Forward a customer's email to{' '}
            <span className="font-semibold text-brix-ink">
              {data?.inbound_address ?? 'the order desk address'}
            </span>{' '}
            and it shows up here already read — account, items and delivery day worked
            out. Nothing reaches Service Fusion until someone accepts it, either from
            the email or from here.
          </p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => {
                const next = new URLSearchParams(params);
                next.set('status', f.key);
                setParams(next, { replace: true });
              }}
              className={cn(
                'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
                status === f.key
                  ? 'bg-brix-primary text-white'
                  : 'bg-brix-surface border border-brix-border text-brix-muted hover:text-brix-ink',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {data && !data.enabled && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          The order desk is <strong>off</strong> — nothing forwarded in will be read.
          Turn it on under{' '}
          <Link to="/admin/company?tab=desk" className="underline font-semibold">
            Company → Order desk
          </Link>
          , where “Check the intake” confirms the rest of the path is live.
        </div>
      )}

      <div className="rounded-2xl border border-brix-border bg-white overflow-hidden">
        {isLoading ? (
          <div className="px-4 py-10 text-center text-sm text-brix-muted">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-brix-muted">
            Nothing here.{' '}
            {status === 'needs_review'
              ? 'Every forwarded email has been dealt with.'
              : 'Try another filter.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-brix-surface text-[11px] uppercase tracking-wide text-brix-muted">
                  <th className="text-left font-semibold px-4 py-2.5">Account</th>
                  <th className="text-left font-semibold px-4 py-2.5">What it is</th>
                  <th className="text-left font-semibold px-4 py-2.5">Lines</th>
                  <th className="text-left font-semibold px-4 py-2.5">Forwarded by</th>
                  <th className="text-left font-semibold px-4 py-2.5">Arrived</th>
                  <th className="text-left font-semibold px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-brix-border hover:bg-brix-surface/60">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-brix-ink">
                        {row.customer_name ?? <span className="text-amber-700">Unknown</span>}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5">{confidenceChip(row)}</div>
                      {row.subject && (
                        <div className="text-[11px] text-brix-muted mt-0.5 max-w-[22rem] truncate">
                          {row.subject}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-brix-muted">
                      {row.intent ? INTENT_LABELS[row.intent] : '—'}
                    </td>
                    <td className="px-4 py-3 text-brix-muted">
                      {row.line_count === 0 ? (
                        '—'
                      ) : (
                        <>
                          {row.line_count}
                          {row.unmatched_count > 0 && (
                            <span className="text-amber-700 font-semibold">
                              {' '}
                              · {row.unmatched_count} to pick
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-brix-muted text-[12px]">
                      {row.from_email ?? '—'}
                      {row.forwarded_from && (
                        <div className="text-[11px] text-brix-muted/80">from {row.forwarded_from}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-brix-muted text-[12px] whitespace-nowrap">
                      {fmtWhen(row.received_at ?? row.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      {statusBadge(row)}
                      {row.error && (
                        <div className="text-[11px] text-red-600 mt-1 max-w-[16rem]">{row.error}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {row.order_id && (
                        <Link
                          to={`/orders/${row.order_id}`}
                          className="text-[12px] font-semibold text-brix-primary hover:underline mr-3"
                        >
                          Order →
                        </Link>
                      )}
                      <Link
                        to={`/admin/order-desk/${row.id}`}
                        className="text-[12px] font-semibold text-brix-primary hover:underline"
                      >
                        {row.status === 'needs_review' ? 'Review →' : 'Details →'}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
